<template>
  <ion-modal :is-open="isOpen" @did-dismiss="$emit('close')">
    <ion-header>
      <ion-toolbar>
        <ion-title>{{ t('searchPOI') }}</ion-title>
        <ion-buttons slot="end">
          <ion-button @click="$emit('close')">{{ t('close') }}</ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content class="ion-padding">
      <!-- Search Options -->
      <div class="space-y-4 mb-4">
        <!-- Category Filter -->
        <div>
          <label class="block text-sm font-medium mb-2">
            {{ t('category') }}
          </label>
          <select v-model="selectedCategory" class="input-field" @change="handleCategoryChange">
            <option :value="null">{{ t('allCategories') }}</option>
            <option value="fuel">⛽ {{ t('fuelAndCharging') }}</option>
            <option value="food">🍽️ {{ t('foodAndDrink') }}</option>
            <option value="accommodation">🏨 {{ t('accommodationCategory') }}</option>
            <option value="attraction">🎯 {{ t('attractionsCategory') }}</option>
            <option value="parking">🅿️ {{ t('parkingCategory') }}</option>
            <option value="rest_area">🚻 {{ t('restAreas') }}</option>
            <option value="shopping">🛍️ {{ t('shoppingCategory') }}</option>
          </select>
        </div>

        <!-- Search Radius -->
        <div>
          <label class="block text-sm font-medium mb-2">
            {{ t('searchRadius') }}: {{ searchRadius / 1000 }} km
          </label>
          <input
            v-model.number="searchRadius"
            type="range"
            min="1000"
            max="20000"
            step="1000"
            class="w-full"
          />
        </div>

        <!-- Search Mode -->
        <div class="flex gap-2">
          <button
            @click="searchMode = 'current'"
            class="flex-1 px-4 py-2 rounded-lg font-medium transition-colors"
            :class="searchMode === 'current' 
              ? 'bg-primary-600 text-white' 
              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'"
          >
            📍 {{ t('nearCurrentLocation') }}
          </button>
          <button
            @click="searchMode = 'route'"
            class="flex-1 px-4 py-2 rounded-lg font-medium transition-colors"
            :class="searchMode === 'route' 
              ? 'bg-primary-600 text-white' 
              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'"
          >
            🛣️ {{ t('alongRoute') }}
          </button>
        </div>

        <!-- Search Button -->
        <button 
          @click="handleSearch" 
          :disabled="isSearching"
          class="w-full btn-primary"
        >
          <ion-spinner v-if="isSearching" class="mr-2" name="crescent"></ion-spinner>
          {{ isSearching ? t('searching') : t('search') }}
        </button>
      </div>

      <!-- Results -->
      <div v-if="searchResults.length > 0" class="space-y-3">
        <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300">
          {{ t('foundResults', { count: searchResults.length }) }}
        </h3>

        <div
          v-for="poi in searchResults"
          :key="poi.id"
          class="poi-card"
          @click="$emit('select-poi', poi)"
        >
          <div class="flex items-start gap-3">
            <!-- Icon -->
            <div class="text-3xl flex-shrink-0">
              {{ getPOIIcon(poi.type) }}
            </div>

            <!-- Info -->
            <div class="flex-1 min-w-0">
              <h4 class="font-semibold text-gray-900 dark:text-gray-100">
                {{ poi.name }}
              </h4>
              
              <p v-if="poi.address" class="text-sm text-gray-600 dark:text-gray-400 truncate">
                📍 {{ poi.address }}
              </p>

              <div class="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
                <span v-if="poi.distance">
                  🚶 {{ formatDistance(poi.distance) }}
                </span>
                <span v-if="poi.phone">
                  📞 {{ poi.phone }}
                </span>
              </div>

              <p v-if="poi.openingHours" class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                🕐 {{ poi.openingHours }}
              </p>
            </div>

            <!-- Quick Add Button -->
            <button
              @click.stop="handleQuickAdd(poi)"
              class="px-3 py-1 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors flex-shrink-0"
            >
              ➕ {{ t('add') }}
            </button>
          </div>

          <!-- Tags -->
          <div v-if="poi.tags && Object.keys(poi.tags).length > 0" class="mt-2 flex flex-wrap gap-1">
            <span
              v-for="(value, key) in getDisplayTags(poi.tags)"
              :key="key"
              class="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs"
            >
              {{ key }}: {{ value }}
            </span>
          </div>
        </div>
      </div>

      <!-- No Results -->
      <div v-else-if="hasSearched && !isSearching" class="text-center py-12">
        <div class="text-6xl mb-3">🔍</div>
        <p class="text-gray-600 dark:text-gray-400">
          {{ t('noResultsFound') }}
        </p>
        <p class="text-sm text-gray-500 dark:text-gray-500 mt-2">
          {{ t('tryDifferentSearch') }}
        </p>
      </div>

      <!-- Initial State -->
      <div v-else-if="!hasSearched" class="text-center py-12">
        <div class="text-6xl mb-3">🗺️</div>
        <p class="text-gray-600 dark:text-gray-400">
          {{ t('selectCategoryAndSearch') }}
        </p>
      </div>
    </ion-content>
  </ion-modal>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonSpinner,
  alertController
} from '@ionic/vue'
import { useI18n } from '@/i18n'
import { searchNearbyPOIs, searchAlongRoute, formatDistance, type POI, type POICategory } from '@/services/poi'
import type { Stop } from '@/types'

interface Props {
  isOpen: boolean
  currentLocation?: { lat: number; lng: number } | null
  routeWaypoints?: { lat: number; lng: number }[]
}

const props = withDefaults(defineProps<Props>(), {
  currentLocation: null,
  routeWaypoints: () => []
})

const emit = defineEmits<{
  close: []
  'select-poi': [poi: POI]
  'add-stop': [stopData: Partial<Stop>]
}>()

const { t } = useI18n()

const selectedCategory = ref<POICategory | null>(null)
const searchRadius = ref(5000) // meters
const searchMode = ref<'current' | 'route'>('current')
const isSearching = ref(false)
const hasSearched = ref(false)
const searchResults = ref<POI[]>([])

async function handleSearch() {
  if (!props.currentLocation && searchMode.value === 'current') {
    alert(t('locationRequired'))
    return
  }

  if (props.routeWaypoints.length === 0 && searchMode.value === 'route') {
    alert(t('routeRequired'))
    return
  }

  isSearching.value = true
  hasSearched.value = true
  searchResults.value = []

  try {
    if (searchMode.value === 'current' && props.currentLocation) {
      // Search near current location
      const results = await searchNearbyPOIs({
        lat: props.currentLocation.lat,
        lng: props.currentLocation.lng,
        radius: searchRadius.value,
        category: selectedCategory.value || undefined,
        limit: 30
      })
      searchResults.value = results
    } else if (searchMode.value === 'route') {
      // Search along route
      const results = await searchAlongRoute(
        props.routeWaypoints,
        selectedCategory.value || undefined,
        searchRadius.value
      )
      searchResults.value = results
    }
  } catch (error: any) {
    console.error('Search failed:', error)
    
    // Show user-friendly error message
    let errorMessage = t('searchFailed')
    if (error.message === 'SERVICE_UNAVAILABLE' || 
        error.message.includes('Gateway Timeout') ||
        error.name === 'AbortError') {
      errorMessage = t('poiServiceUnavailable')
    }
    
    const errorAlert = await alertController.create({
      header: t('error'),
      message: errorMessage,
      buttons: ['OK']
    })
    await errorAlert.present()
  } finally {
    isSearching.value = false
  }
}

function handleCategoryChange() {
  // Auto-search when category changes
  if (hasSearched.value) {
    handleSearch()
  }
}

function handleQuickAdd(poi: POI) {
  const stopData: Partial<Stop> = {
    name: poi.name,
    lat: poi.lat,
    lng: poi.lng,
    type: poi.type as any,
    address: poi.address,
    notes: buildPOINotes(poi)
  }

  emit('add-stop', stopData)
  emit('close')
}

function buildPOINotes(poi: POI): string {
  const parts = []
  
  if (poi.phone) parts.push(`📞 ${poi.phone}`)
  if (poi.website) parts.push(`🌐 ${poi.website}`)
  if (poi.openingHours) parts.push(`🕐 ${poi.openingHours}`)
  
  return parts.join('\n')
}

function getPOIIcon(type: string): string {
  const icons: Record<string, string> = {
    gas_station: '⛽',
    ev_charging: '⚡',
    restaurant: '🍽️',
    cafe: '☕',
    fast_food: '🍔',
    hotel: '🏨',
    motel: '🛏️',
    camping: '⛺',
    attraction: '🎯',
    museum: '🏛️',
    viewpoint: '🏔️',
    parking: '🅿️',
    rest_area: '🚻',
    shopping: '🛍️',
    other: '📍'
  }
  return icons[type] || '📍'
}

function getDisplayTags(tags: Record<string, string>): Record<string, string> {
  const displayTags: Record<string, string> = {}
  const interestingKeys = ['brand', 'operator', 'cuisine', 'stars', 'capacity']
  
  for (const key of interestingKeys) {
    if (tags[key]) {
      displayTags[key] = tags[key]
    }
  }
  
  return displayTags
}
</script>

<style scoped>
.input-field {
  @apply w-full px-3 py-2 border border-gray-300 dark:border-gray-600 
         rounded-lg bg-white dark:bg-gray-800 
         text-gray-900 dark:text-gray-100
         focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent;
}

.btn-primary {
  @apply px-6 py-3 bg-primary-600 text-white rounded-lg font-medium
         hover:bg-primary-700 transition-colors inline-flex items-center justify-center
         disabled:opacity-50 disabled:cursor-not-allowed;
}

.poi-card {
  @apply bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 
         p-4 cursor-pointer transition-all hover:shadow-md hover:border-primary-500;
}

.space-y-3 > * + * {
  margin-top: 0.75rem;
}

.space-y-4 > * + * {
  margin-top: 1rem;
}
</style>
